import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';

interface RequestApproveTabProps {
  request: any;
  requestId: string;
  onOpenEditModal: (selectedSuppliers: any[]) => void;
  onRequestUpdate?: () => void;
}

interface SupplierDropdownSectionProps {
  title: string;
  suppliers: any[];
  selectedSuppliers: any[];
  onSupplierSelection: (supplier: any, isSelected: boolean) => void;
  supplierType: 'ai' | 'manual' | 'local';
  isLoading?: boolean;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchResults?: any[];
  isSearching?: boolean;
  showSearchResults?: boolean;
}

const SupplierDropdownSection: React.FC<SupplierDropdownSectionProps> = ({
  title,
  suppliers,
  selectedSuppliers,
  onSupplierSelection,
  supplierType,
  isLoading = false,
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  searchResults = [],
  isSearching = false,
  showSearchResults = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const getSupplierKey = (supplier: any, index: number) => {
    if (supplierType === 'local') {
      const supplierId = supplier.supplier_id || supplier.id || 'unknown';
      const productId = supplier.product_id || 'unknown';
      return `local-${supplierId}-${productId}-${index}`;
    }
    // For AI and Manual suppliers, use company_name + index for unique keys
    const id = supplier.id || supplier.company_name || `unknown-${index}`;
    return `${supplierType}-${id}-${index}`;
  };

  const isSupplierSelected = (supplier: any) => {
    if (supplierType === 'local') {
      const isSelected = selectedSuppliers.some(s => 
        s.supplier_id === supplier.supplier_id && s.product_id === supplier.product_id
      );
      console.log('🔍 DEBUG - isSupplierSelected (local) - Supplier:', supplier.supplier_name, 'Is selected:', isSelected);
      return isSelected;
    }
    
    // For AI and Manual suppliers, check by company_name since id might not exist
    // But we need to be more specific to avoid false matches
    const isSelected = selectedSuppliers.some(s => {
      // Check if it's the exact same supplier object or has same company_name + email combination
      const sameCompany = s.company_name === supplier.company_name;
      const sameEmail = s.email === supplier.email;
      const sameId = s.id === supplier.id;
      
      console.log('🔍 DEBUG - isSupplierSelected - Comparing:', {
        selected: { company: s.company_name, email: s.email, id: s.id },
        current: { company: supplier.company_name, email: supplier.email, id: supplier.id },
        sameCompany, sameEmail, sameId
      });
      
      return sameCompany && sameEmail; // Both company_name AND email must match
    });
    
    console.log('🔍 DEBUG - isSupplierSelected - Supplier:', supplier.company_name, 'ID:', supplier.id, 'Is selected:', isSelected);
    return isSelected;
  };

  const renderSupplierCard = (supplier: any, index: number) => {
    const isSelected = isSupplierSelected(supplier);
    const supplierKey = getSupplierKey(supplier, index);

    if (supplierType === 'local') {
      return (
        <div key={supplierKey} className="supplier-card approve-supplier">
          <div className="supplier-header">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSupplierSelection(supplier, e.target.checked)}
              className="supplier-checkbox"
            />
            <div className="supplier-info">
              <h5>{supplier.supplier_name}</h5>
              <p className="supplier-email">{supplier.supplier_email}</p>
              <p className="supplier-region">{supplier.supplier_region}</p>
            </div>
            <div className="match-score">
              <span className="score-badge">{supplier.match_score}%</span>
            </div>
          </div>
          
          <div className="product-info">
            <h6>{t('common.product')}: {supplier.product_name}</h6>
            <p className="product-brand">{t('common.brand')}: {supplier.product_brand || 'N/A'}</p>
            <p className="product-price">
              {t('common.price')}: {supplier.unit_price} {supplier.currency}
            </p>
            {supplier.delivery_time && (
              <p className="product-delivery">{t('common.delivery_time')}: {supplier.delivery_time}</p>
            )}
            {supplier.minimum_order && (
              <p className="product-min-order">{t('common.minimum_order')}: {supplier.minimum_order}</p>
            )}
            {supplier.payment_terms && (
              <p className="product-payment">{t('common.payment_terms')}: {supplier.payment_terms}</p>
            )}
            {supplier.product_description && (
              <p className="product-description">
                {supplier.product_description}
              </p>
            )}
            
            {/* OCR Historical Data */}
            {supplier.ocr_unit_price && (
              <div className="ocr-historical-data">
                <h6>{t('common.ocr_historical_data')}:</h6>
                <p className="ocr-price">
                  {t('common.ocr_price')}: {supplier.ocr_unit_price} {supplier.ocr_currency || supplier.currency}
                </p>
                {supplier.ocr_supplier_name && (
                  <p className="ocr-supplier">{t('common.ocr_supplier')}: {supplier.ocr_supplier_name}</p>
                )}
                {supplier.ocr_purchase_date && (
                  <p className="ocr-date">
                    {t('common.ocr_date')}: {new Date(supplier.ocr_purchase_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // AI and Manual suppliers
    return (
      <div key={supplierKey} className="supplier-card approve-supplier">
        <div className="supplier-header">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSupplierSelection(supplier, e.target.checked)}
            className="supplier-checkbox"
          />
          <div className="supplier-info">
            <h5>{supplier.company_name}</h5>
            <p className="supplier-email">{supplier.email}</p>
            <p className="supplier-region">{supplier.region}</p>
          </div>
          <div className="supplier-badges">
            <span className={`type-badge ${supplierType}-badge`}>
              {supplierType === 'ai' ? 'AI' : 'Manual'}
            </span>
            {supplier.rating && (
              <span className="rating-badge">⭐ {supplier.rating}</span>
            )}
          </div>
        </div>
        
        <div className="product-info">
          {supplier.products && supplier.products.map((product: any, productIndex: number) => (
            <div key={productIndex} className="product-item">
              <h6>{t('common.product')}: {product.name}</h6>
              {product.description && (
                <p className="product-description">{product.description}</p>
              )}
              <p className="product-price">
                {t('common.price')}: {product.price}
              </p>
              {product.link && (
                <p className="product-link">
                  <a href={product.link} target="_blank" rel="noopener noreferrer">
                    {t('common.view_product')}
                  </a>
                </p>
              )}
            </div>
          ))}
          
          {supplier.delivery_time && (
            <p className="product-delivery">{t('common.delivery_time')}: {supplier.delivery_time}</p>
          )}
          
          {supplier.website && (
            <p className="supplier-website">
              <a href={supplier.website} target="_blank" rel="noopener noreferrer">
                {t('common.website')}: {supplier.website}
              </a>
            </p>
          )}
        </div>
      </div>
    );
  };

  // Determine which suppliers to show
  const suppliersToShow = showSearchResults ? searchResults : suppliers;
  const suppliersCount = showSearchResults ? searchResults.length : suppliers.length;

  return (
    <div className="supplier-dropdown-section">
      <div className="dropdown-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h4>{title} ({suppliersCount})</h4>
        <span className={`dropdown-arrow ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </span>
      </div>
      
      {isExpanded && (
        <div className="suppliers-content">
          {/* Search Input for Local Suppliers */}
          {showSearch && (
            <div className="supplier-search-section">
              <div className="search-input-container">
                <input
                  type="text"
                  placeholder={t('common.search_suppliers')}
                  value={searchQuery}
                  onChange={onSearchChange}
                  className="supplier-search-input"
                />
                {isSearching && (
                  <div className="search-loading">
                    <span className="loading-spinner"></span>
                  </div>
                )}
              </div>
              {showSearchResults && (
                <div className="search-results-info">
                  <p>{t('common.search_results')}: {searchResults.length}</p>
                </div>
              )}
            </div>
          )}

          {/* Suppliers Grid */}
          {isLoading ? (
            <div className="loading-state">
              <p>{t('common.loading')}...</p>
            </div>
          ) : suppliersToShow.length > 0 ? (
            <div className="suppliers-grid">
              {suppliersToShow.map((supplier, index) => renderSupplierCard(supplier, index))}
            </div>
          ) : (
            <div className="no-results">
              <p>
                {showSearchResults 
                  ? t('common.no_search_results') 
                  : t('common.no_suppliers_found')
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RequestApproveTab: React.FC<RequestApproveTabProps> = ({
  request,
  requestId,
  onOpenEditModal,
  onRequestUpdate
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  // Approve modal states - now managed internally
  const [approveModal, setApproveModal] = useState({
    isOpen: false,
    isLoading: false,
    localSuppliers: [] as any[],
    aiSuppliers: [] as any[],
    manualSuppliers: [] as any[],
    selectedSuppliers: [] as any[]
  });

  // Local supplier search states
  const [localSearch, setLocalSearch] = useState({
    isSearching: false,
    searchQuery: '',
    searchResults: [] as any[],
    showSearchResults: false,
    searchTimeout: null as NodeJS.Timeout | null
  });

  // Load local suppliers when component mounts
  useEffect(() => {
    if (requestId && token) {
      handleOpenApproveModal();
    }
  }, [requestId, token]);

  // Load manual suppliers when request changes
  useEffect(() => {
    if (request && request.manual_suppliers) {
      console.log('🔍 DEBUG - useEffect - Loading manual suppliers:', request.manual_suppliers);
      console.log('🔍 DEBUG - useEffect - Manual suppliers count:', request.manual_suppliers.length);
      setApproveModal(prev => ({
        ...prev,
        manualSuppliers: request.manual_suppliers || []
      }));
    } else {
      console.log('🔍 DEBUG - useEffect - No manual suppliers found in request:', request);
    }
  }, [request]);


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (localSearch.searchTimeout) {
        clearTimeout(localSearch.searchTimeout);
      }
    };
  }, [localSearch.searchTimeout]);

  // Open approve modal and load all suppliers
  const handleOpenApproveModal = async () => {
    if (!token || !requestId) return;
    
    setApproveModal(prev => ({ ...prev, isOpen: true, isLoading: true }));
    
    try {
      // Load local suppliers
      const localResponse = await fetch(API_ENDPOINTS.LOCAL_SUPPLIERS(requestId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let localSuppliers: any[] = [];
      if (localResponse.ok) {
        const localResult = await localResponse.json();
        localSuppliers = localResult.data || [];
      }

      // Get AI and Manual suppliers from request data
      const aiSuppliers = request?.analysis_result?.ai_suppliers || [];
      const manualSuppliers = request?.manual_suppliers || [];
      
      console.log('🔍 DEBUG - handleOpenApproveModal - Request data:', request);
      console.log('🔍 DEBUG - handleOpenApproveModal - Manual suppliers:', manualSuppliers);
      console.log('🔍 DEBUG - handleOpenApproveModal - Manual suppliers count:', manualSuppliers.length);
      console.log('🔍 DEBUG - handleOpenApproveModal - AI suppliers:', aiSuppliers);

      setApproveModal(prev => ({
        ...prev,
        localSuppliers,
        aiSuppliers,
        manualSuppliers,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setApproveModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Close approve modal
  const handleCloseApproveModal = () => {
    setApproveModal({
      isOpen: false,
      isLoading: false,
      localSuppliers: [],
      aiSuppliers: [],
      manualSuppliers: [],
      selectedSuppliers: []
    });
  };

  // Handle supplier selection
  const handleSupplierSelection = (supplier: any, isSelected: boolean) => {
    console.log('🔍 DEBUG - handleSupplierSelection - Supplier:', supplier);
    console.log('🔍 DEBUG - handleSupplierSelection - Is selected:', isSelected);
    console.log('🔍 DEBUG - handleSupplierSelection - Supplier ID:', supplier.id);
    console.log('🔍 DEBUG - handleSupplierSelection - Supplier company_name:', supplier.company_name);
    
    setApproveModal(prev => {
      if (isSelected) {
        // Add supplier with type information
        const supplierWithType = {
          ...supplier,
          supplierType: supplier.supplier_id ? 'local' : (supplier.id?.startsWith('manual_') ? 'manual' : 'ai')
        };
        console.log('🔍 DEBUG - handleSupplierSelection - Adding supplier with type:', supplierWithType.supplierType);
        return {
          ...prev,
          selectedSuppliers: [...prev.selectedSuppliers, supplierWithType]
        };
      } else {
        // Remove supplier based on appropriate key
        console.log('🔍 DEBUG - handleSupplierSelection - Removing supplier:', supplier.company_name);
        return {
          ...prev,
          selectedSuppliers: prev.selectedSuppliers.filter(s => {
            if (s.supplier_id) {
              return !(s.supplier_id === supplier.supplier_id && s.product_id === supplier.product_id);
            }
            // For AI and Manual suppliers, check by company_name AND email
            const sameCompany = s.company_name === supplier.company_name;
            const sameEmail = s.email === supplier.email;
            const sameId = s.id === supplier.id;
            
            console.log('🔍 DEBUG - handleSupplierSelection - Filtering out:', {
              selected: { company: s.company_name, email: s.email, id: s.id },
              current: { company: supplier.company_name, email: supplier.email, id: supplier.id },
              shouldRemove: sameCompany && sameEmail
            });
            
            return !(sameCompany && sameEmail); // Remove if both company_name AND email match
          })
        };
      }
    });
  };

  // Search local suppliers manually
  const handleLocalSupplierSearch = async (query: string) => {
    if (!token || !query.trim()) {
      setLocalSearch(prev => ({ ...prev, searchResults: [], showSearchResults: false }));
      return;
    }

    setLocalSearch(prev => ({ ...prev, isSearching: true }));

    try {
      const response = await fetch(API_ENDPOINTS.SUPPLIER_SEARCH, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: query.trim() })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Search response:', result);
        console.log('🔍 Search results count:', result.data?.length || 0);
        setLocalSearch(prev => ({
          ...prev,
          searchResults: result.data || [],
          showSearchResults: true,
          isSearching: false
        }));
      } else {
        console.log('❌ Search failed:', response.status);
        setLocalSearch(prev => ({ ...prev, isSearching: false }));
      }
    } catch (error) {
      console.error('Error searching local suppliers:', error);
      setLocalSearch(prev => ({ ...prev, isSearching: false }));
    }
  };

  // Handle search input change with debounce
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setLocalSearch(prev => ({ ...prev, searchQuery: query }));
    
    // Clear previous timeout
    if (localSearch.searchTimeout) {
      clearTimeout(localSearch.searchTimeout);
    }
    
    // Set new timeout
    const timeoutId = setTimeout(() => {
      handleLocalSupplierSearch(query);
    }, 500);
    
    setLocalSearch(prev => ({ ...prev, searchTimeout: timeoutId }));
  };

  // Approve with selected suppliers - opens Edit Product Modal
  const handleApproveWithSuppliers = () => {
    console.log('🔍 DEBUG - handleApproveWithSuppliers - Selected suppliers:', approveModal.selectedSuppliers);
    console.log('🔍 DEBUG - handleApproveWithSuppliers - Selected suppliers count:', approveModal.selectedSuppliers.length);
    
    if (approveModal.selectedSuppliers.length === 0) {
      alert('Please select at least one supplier');
      return;
    }

    // Open Edit Product Modal instead of directly approving
    onOpenEditModal(approveModal.selectedSuppliers);
    
    // Close the approve modal
    handleCloseApproveModal();
  };

  return (
    <div className="approve-content">
      <div className="approve-header">
        <h3 className="section-title-black">{t('common.approve_request')} - {t('common.select_suppliers')}</h3>
      </div>

      {approveModal.isLoading ? (
        <div className="loading-state">
          <p>{t('common.loading_suppliers')}</p>
        </div>
      ) : (
        <div className="approve-suppliers-section">
          {/* Local Suppliers */}
          <SupplierDropdownSection
            title={t('common.local_suppliers')}
            suppliers={approveModal.localSuppliers}
            selectedSuppliers={approveModal.selectedSuppliers}
            onSupplierSelection={handleSupplierSelection}
            supplierType="local"
            isLoading={approveModal.isLoading}
            showSearch={true}
            searchQuery={localSearch.searchQuery}
            onSearchChange={handleSearchInputChange}
            searchResults={localSearch.searchResults}
            isSearching={localSearch.isSearching}
            showSearchResults={localSearch.showSearchResults}
          />

          {/* AI Suppliers */}
          <SupplierDropdownSection
            title={t('common.ai_recommended_suppliers')}
            suppliers={approveModal.aiSuppliers}
            selectedSuppliers={approveModal.selectedSuppliers}
            onSupplierSelection={handleSupplierSelection}
            supplierType="ai"
            isLoading={approveModal.isLoading}
          />

          {/* Manual Suppliers */}
          <SupplierDropdownSection
            title={t('common.manual_suppliers')}
            suppliers={approveModal.manualSuppliers}
            selectedSuppliers={approveModal.selectedSuppliers}
            onSupplierSelection={handleSupplierSelection}
            supplierType="manual"
            isLoading={approveModal.isLoading}
          />
          
          <div className="selection-summary">
            <p>
              <strong>{t('common.selected')}:</strong> {t('common.selected_suppliers', { count: approveModal.selectedSuppliers.length })}
            </p>
          </div>
          
          <div className="approve-actions">
            <button 
              className="approve-btn"
              onClick={handleApproveWithSuppliers}
              disabled={approveModal.selectedSuppliers.length === 0}
            >
              {t('common.approve_with_suppliers')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestApproveTab;
